import { supabase } from "@/integrations/supabase/client";

export interface ProductVariation {
  id: string;
  product_id: string;
  variation_name: string;
  created_at: string;
}

export interface ProductWithVariations {
  id: string;
  name: string;
  variations: ProductVariation[];
}

// Cache for quick lookups
let variationCache: Map<string, string> | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get all product variations grouped by product
 */
export async function getProductVariationsGrouped(): Promise<ProductWithVariations[]> {
  const { data: products, error: productsError } = await supabase
    .from("products")
    .select("id, name")
    .order("name");

  if (productsError) throw productsError;

  const { data: variations, error: variationsError } = await supabase
    .from("product_variations")
    .select("*")
    .order("variation_name");

  if (variationsError) throw variationsError;

  return (products || []).map((product) => ({
    ...product,
    variations: (variations || []).filter((v) => v.product_id === product.id),
  }));
}

/**
 * Build and cache the variation -> canonical name mapping
 */
async function buildVariationCache(): Promise<Map<string, string>> {
  const now = Date.now();
  if (variationCache && now - cacheTimestamp < CACHE_DURATION) {
    return variationCache;
  }

  const { data: products, error: productsError } = await supabase
    .from("products")
    .select("id, name");

  if (productsError) throw productsError;

  const { data: variations, error: variationsError } = await supabase
    .from("product_variations")
    .select("product_id, variation_name");

  if (variationsError) throw variationsError;

  const productMap = new Map((products || []).map((p) => [p.id, p.name]));
  const cache = new Map<string, string>();

  // Add product names as variations of themselves
  (products || []).forEach((p) => {
    cache.set(p.name.toLowerCase().trim(), p.name);
  });

  // Add all variations
  (variations || []).forEach((v) => {
    const productName = productMap.get(v.product_id);
    if (productName) {
      cache.set(v.variation_name.toLowerCase().trim(), productName);
    }
  });

  variationCache = cache;
  cacheTimestamp = now;
  return cache;
}

/**
 * Get canonical product name for any variation
 */
export async function getCanonicalProductName(productName: string): Promise<string> {
  if (!productName) return productName;

  try {
    const cache = await buildVariationCache();
    const normalized = productName.toLowerCase().trim();
    return cache.get(normalized) || productName;
  } catch (error) {
    console.error("Error getting canonical product name:", error);
    return productName;
  }
}

/**
 * Get all canonical product names (products from the products table)
 */
export async function getCanonicalProductNames(): Promise<string[]> {
  const { data, error } = await supabase
    .from("products")
    .select("name")
    .order("name");

  if (error) throw error;
  return (data || []).map((p) => p.name);
}

/**
 * Get all variations for a canonical product name
 */
export async function getProductVariations(canonicalName: string): Promise<string[]> {
  const { data: product, error: productError } = await supabase
    .from("products")
    .select("id")
    .eq("name", canonicalName)
    .single();

  if (productError || !product) return [canonicalName];

  const { data: variations, error: variationsError } = await supabase
    .from("product_variations")
    .select("variation_name")
    .eq("product_id", product.id);

  if (variationsError) return [canonicalName];

  return [canonicalName, ...(variations || []).map((v) => v.variation_name)];
}

/**
 * Add a new variation for a product
 */
export async function addProductVariation(
  productId: string,
  variationName: string
): Promise<ProductVariation> {
  const { data, error } = await supabase
    .from("product_variations")
    .insert({ product_id: productId, variation_name: variationName.trim() })
    .select()
    .single();

  if (error) throw error;
  
  // Invalidate cache
  variationCache = null;
  
  return data;
}

/**
 * Delete a variation
 */
export async function deleteProductVariation(variationId: string): Promise<void> {
  const { error } = await supabase
    .from("product_variations")
    .delete()
    .eq("id", variationId);

  if (error) throw error;
  
  // Invalidate cache
  variationCache = null;
}

/**
 * Invalidate the cache (call after bulk operations)
 */
export function invalidateVariationCache(): void {
  variationCache = null;
}

/**
 * Check if a product name matches any variation of a canonical name
 */
export async function isProductVariation(
  productName: string,
  canonicalName: string
): Promise<boolean> {
  const variations = await getProductVariations(canonicalName);
  return variations.some(
    (v) => v.toLowerCase().trim() === productName.toLowerCase().trim()
  );
}
